import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { GaugeChartService } from "./gauge-chart.service";
import {
  FilterLoadedState,
  FilterLoadingState,
  MoodLoadedState,
  MoodLoadingState,
} from "./gauge-chart.state";
import { Store } from "../../@core/store/store";

interface BuDuMap {
  [key: string]: string[];
}

interface FilterData {
  bU_DU_Map: BuDuMap;
  dG_DU_Map: BuDuMap;
  deliveryUnits: string[];
  businessUnits: string[];
  baseLocations: string[];
  grades: string[];
  country_Location_Map: BuDuMap;
  country: string[];
}

export interface MoodStat {
  percentage: number;
  count: number;
}

export interface MoodPercentage {
  totalCount: number;
  excellent: MoodStat;
  okay: MoodStat;
  notOkay: MoodStat;
  happy: MoodStat;
}

@Component({
  selector: "app-gauge-chart",
  templateUrl: "./gauge-chart.component.html",
  styleUrls: ["./gauge-chart.component.scss"],
})
export class GaugeChartComponent implements OnInit {
  // ---------------- DATE ----------------
  fromDateValue: Date | null = this.getTenDaysAgoDateObject();
  toDateValue!: Date;
  today!: Date;

  // ---------------- FILTERS ----------------
  selectedBU: string[] = [];
  selectedDG: string[] = [];
  selectedDU: string[] = [];
  selectedGender: string[] = [];
  selectedLocation: string[] = [];
  selectedGrade: string[] = [];
  selectedCountry: string[] = [];

  // ---------------- DATA ----------------
  filterData: FilterData = {
    bU_DU_Map: {},
    dG_DU_Map: {},
    deliveryUnits: [],
    businessUnits: [],
    baseLocations: [],
    grades: [],
    country_Location_Map: {},
    country: []
  };

  filteredDeliveryUnits: string[] = [];
  filteredDeliveryGroups: string[] = [];
  filterBaseLocation: String[] = [];

  graphData!: MoodPercentage;
  excellentData: any;
  okData: any;
  notOkData: any;
  happyData: any;

  gaugeOption: any = {};
  formattedDate: string = "";

  constructor(
    private gaugeChartService: GaugeChartService,
    private cdRef: ChangeDetectorRef
  ) { }

  // ---------------- INIT ----------------
  ngOnInit(): void {
    this.today = new Date();
    this.today.setDate(this.today.getDate() - 2);
    this.toDateValue = this.today;

    this.formattedDate = this.today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });

    this.fetchFilterData();
    this.fetchGraphData();
  }

  // ---------------- FILTER ACTIVE CHECK ----------------
  isAnyFilterActive(): boolean {
    const hasArrayValue = (arr?: any[]) =>
      Array.isArray(arr) && arr.length > 0;

    const isBUActive = hasArrayValue(this.selectedBU);
    const isDGActive = hasArrayValue(this.selectedDG);
    const isDUActive = hasArrayValue(this.selectedDU);

    const isGenderActive = hasArrayValue(this.selectedGender)

    const isLocationActive = hasArrayValue(this.selectedLocation);

    const isGradeActive = hasArrayValue(this.selectedGrade)
    const isCountryActive = hasArrayValue(this.selectedCountry)


    const defaultFromDate =
      this.getTenDaysAgoDateObject().toDateString();
    const defaultToDate = this.today.toDateString();

    const isDateActive =
      !!this.fromDateValue &&
      !!this.toDateValue &&
      (this.fromDateValue.toDateString() !== defaultFromDate ||
        this.toDateValue.toDateString() !== defaultToDate);

    return (
      isBUActive ||
      isDGActive ||
      isDUActive ||
      isGenderActive ||
      isLocationActive ||
      isGradeActive ||
      isDateActive ||
      isCountryActive
    );
  }

  // ---------------- CLEAR FILTER ----------------
  clearFilter(): void {
    this.selectedBU = [];
    this.selectedDG = [];
    this.selectedDU = [];
    this.selectedLocation = [];
    this.selectedGender = [];
    this.selectedGrade = [];
    this.selectedCountry = [];

    this.fromDateValue = this.getTenDaysAgoDateObject();
    this.toDateValue = this.today;

    this.updateDeliveryFilters();

    this.onFilterChange();
  }

  initializeCountry(): void {
    const map = this.filterData.country_Location_Map;

    // Business Units
    this.filterData.country = Object.keys(map).sort();

    // Flatten DUs WITHOUT .flat()
    const allBaseLocation = Object.values(map).reduce(
      (acc: string[], curr: string[]) => acc.concat(curr),
      []
    );

    // Remove duplicates
    this.filterData.baseLocations = Array.from(new Set(allBaseLocation)).sort();

    // Initialize filtered list
    this.filterBaseLocation = [...this.filterData.baseLocations];
  }

  initializeDeliveryUnits(): void {
    const map = this.filterData.bU_DU_Map;

    // Business Units
    this.filterData.businessUnits = Object.keys(map).sort();

    // Flatten DUs WITHOUT .flat()
    const allDUs = Object.values(map).reduce(
      (acc: string[], curr: string[]) => acc.concat(curr),
      []
    );

    // Remove duplicates
    this.filterData.deliveryUnits = Array.from(new Set(allDUs)).sort();

    // Initialize filtered list
    this.filteredDeliveryUnits = [...this.filterData.deliveryUnits];
    this.updateDeliveryFilters();
  }

  onCountryChange(selectedCountries: string[]): void {
    this.selectedLocation = [];

    if (!selectedCountries || selectedCountries.length === 0) {
      this.filterBaseLocation = [...this.filterData.baseLocations];
      this.onFilterChange();
      return;
    }

    const locationSet = new Set<string>();

    selectedCountries.forEach((country) => {
      const locations = this.filterData.country_Location_Map[country] || [];
      locations.forEach((loc) => locationSet.add(loc));
    });

    this.filterBaseLocation = Array.from(locationSet).sort();
    this.onFilterChange();
  }

  filterDeliveryUnits(selectedBUs: string[]): void {
    this.selectedBU = selectedBUs || [];
    this.selectedDG = [];
    this.selectedDU = [];
    this.updateDeliveryFilters();
    this.onFilterChange();
  }

  // ---------------- DATE ----------------
  onDateChange(): void {
    if (this.fromDateValue && this.toDateValue) {
      if (this.fromDateValue > this.toDateValue) {
        this.fromDateValue = null;
      }
    }
    this.onFilterChange();
  }

  getTenDaysAgoDateObject(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 12);
    return d;
  }

  formatLocalDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  // ---------------- API ----------------
  fetchFilterData(): void {
    this.gaugeChartService.fetchGraphFilterData().subscribe(
      (state: Store.State) => {
        if (state instanceof FilterLoadingState) return;

        if (state instanceof FilterLoadedState) {
          this.filterData = state.data;
          this.initializeDeliveryUnits();
          this.initializeCountry();
          this.cdRef.detectChanges();
        }
      }
    );
  }

  fetchGraphData(): void {
    const fromDate = this.fromDateValue
      ? this.formatLocalDate(this.fromDateValue)
      : this.formatLocalDate(this.getTenDaysAgoDateObject());

    const toDate = this.toDateValue
      ? this.formatLocalDate(this.toDateValue)
      : this.formatLocalDate(new Date());

    const payload = {
      bu: this.selectedBU.length ? this.selectedBU.join(', ') : '',
      du: this.selectedDU.length ? this.selectedDU.join(', ')
        : this.selectedDG.length ? this.filteredDeliveryUnits.join(', ') : '',
      gender: this.selectedGender.length ? this.selectedGender.join(', ') : '',
      fromDate: fromDate,
      toDate: toDate,
      band: this.selectedGrade.length ? this.selectedGrade.join(', ') : '',
      baseLocation: this.selectedLocation.length ? this.selectedLocation.join(', ') : '',
      country: this.selectedCountry?.length ? this.selectedCountry.join(', ') : '',
      deliveryGroup: this.selectedDG.length ? this.selectedDG.join(', ') : '',
    };

    this.gaugeChartService
      .fetchMoodFiltrationData(payload)
      .subscribe((state: Store.State) => {
        if (state instanceof MoodLoadingState) return;

        if (state instanceof MoodLoadedState) {
          this.graphData = state.data;
          this.excellentData = this.graphData.excellent;
          this.okData = this.graphData.okay;
          this.notOkData = this.graphData.notOkay;
          this.happyData = this.graphData.happy;
          this.updateGaugeOptions();
          this.cdRef.detectChanges();
        }
      });
  }
  onBUChange(): void {
    this.updateDeliveryFilters();
    this.onFilterChange();
  }

  onDGChange(): void {
    this.updateDeliveryFilters();
    this.onFilterChange();
  }

  private updateDeliveryFilters(): void {
    const deliveryUnits = this.selectedBU.length
      ? Array.from(new Set(this.selectedBU.reduce<string[]>(
        (units, bu) => units.concat(this.filterData.bU_DU_Map[bu] || []), []
      ))).sort()
      : [...this.filterData.deliveryUnits];
    const groupMap = this.filterData.dG_DU_Map || {};

    const groupLists = this.selectedBU.length
      ? this.selectedBU.map(bu => groupMap[bu] || [])
      : Object.values(groupMap);
    this.filteredDeliveryGroups = Array.from(new Set(groupLists.reduce<string[]>(
      (groups, values) => groups.concat(values), []
    ))).sort();
    this.selectedDG = this.selectedDG.filter(group => this.filteredDeliveryGroups.includes(group));
    this.filteredDeliveryUnits = this.selectedDG.length
      ? deliveryUnits.filter(du => this.selectedDG.includes(du))
      : deliveryUnits;
    this.selectedDU = this.selectedDU.filter(du =>
      this.filteredDeliveryUnits.includes(du)
    );
  }

  onFilterChange(): void {
    this.fetchGraphData();
  }


  updateGaugeOptions() {
    const excellentCount = this.graphData?.excellent?.count ?? 0;
    const okayCount = this.graphData?.okay?.count ?? 0;
    const notOkayCount = this.graphData?.notOkay?.count ?? 0;
    const happyCount = this.graphData?.happy?.count ?? 0;

    const excellentPer = this.graphData?.excellent?.percentage ?? 0;
    const okayPer = this.graphData?.okay?.percentage ?? 0;
    const notOkayPer = this.graphData?.notOkay?.percentage ?? 0;
    const happyPer = this.graphData?.happy?.percentage ?? 0;

    const imgMap: any = {
      Excellent: "excellentnew.png",
      Okay: "okay.png",
      "Could be better": "better.png",
      Happy: "happy.png"
    };

    this.gaugeOption = {
      tooltip: {
        trigger: "item",
        borderRadius: 20,
        padding: 5,
        backgroundColor: "#fff",
        formatter: (params: any) => {
          const imgFile = imgMap[params.name] || "default.png";
          console.log(`imgFile for tooltip:`, imgFile);
          console.log(`imgFileTooltip params:`, params);

          return `
            <div style="display:flex;align-items:center;gap:6px;">
              <img style="width:24px;height:24px;" 
                   src="/assets/feedback-icons/${imgFile}"/>
                   (<strong>${params.name}</strong>)${params.value}% 
            </div>
          `;
        },
      },

      legend: {
        // Desktop Position: Vertical, on the right side
        orient: "vertical",
        right: "10%",
        top: "20%",
        itemWidth: 24,
        itemHeight: 24,
        selectedMode: false,
        textStyle: {
          color: "#003F72",
          fontSize: 14,
        },
        formatter: (name: string) => {
          let count = 0;
          let percent = 0;

          if (name === "Excellent") {
            count = excellentCount;
            percent = excellentPer;
          } else if (name === "Okay") {
            count = okayCount;
            percent = okayPer;
          } else if (name === "Could be better") {
            count = notOkayCount;
            percent = notOkayPer;
          } else if (name === "Happy") {
            count = happyCount;
            percent = happyPer;
          }


          return `(${Math.round(percent)}%) ${name} (${count.toLocaleString('en-US')})`;
        },

        data: [
          {
            name: "Excellent", // Must match series name
            icon: `image:///assets/feedback-icons/${imgMap.Excellent}`,
          },
          {
            name: "Happy", // Must match series name
            icon: `image:///assets/feedback-icons/${imgMap.Happy}`,
          },
          {
            name: "Okay", // Must match series name
            icon: `image:///assets/feedback-icons/${imgMap.Okay}`,
          },
          {
            name: "Could be better", // Must match series name
            icon: `image:///assets/feedback-icons/${imgMap["Could be better"]}`,
          }
        ],
      },

      series: [
        {
          name: "Mood",
          type: "pie",
          // Desktop Position: Chart shifted left to 35% horizontal position
          center: ["34.5%", "53%"],
          radius: ["85%", "100%"],
          startAngle: 210,
          endAngle: -30,

          itemStyle: {
            borderRadius: 8,
            borderColor: "#fff",
            borderWidth: 2,
          },

          label: {
            show: false,
            position: "outside",
            formatter: (params: any) => (params.value === 0 ? "" : params.name),
            fontSize: 14,
            fontWeight: 600,
            color: "#003F72",
          },

          labelLine: { show: false },

          data: [
            {
              value: Math.round(this.graphData.excellent.percentage),
              name: "Excellent",
              itemStyle: { color: "#DF8211" },
            },
            {
              value: Math.round(this.graphData.happy.percentage),
              name: "Happy",
              itemStyle: { color: "#2DC725" },
            },
            {
              value: Math.round(this.graphData.okay.percentage),
              name: "Okay",
              itemStyle: { color: "#298EBF" },
            },
            {
              value: Math.round(this.graphData.notOkay.percentage),
              name: "Could be better",
              itemStyle: { color: "#EB1716" },
            },
          ],
        },
      ],

      // Defined as an array to position the chart center and divider line independently
      graphic: [
        // 1. Group for Chart Center (Circle and Text) - aligned with the pie chart center (35%)
        {
          type: "group",
          left: "51%",
          top: "19%",
          children: [
            {
              type: "circle",
              shape: { r: 60 },
              style: {
                fill: "#ffffff",
                shadowColor: "rgba(0,0,0,0.15)",
                shadowBlur: 15,
              },
            },
            {
              type: "text",
              left: "center", // Centered within the group
              top: "center",
              style: {
                // Define the combined text string using rich tags and line breaks (\n)
                // Format: {styleName|text content}
                text: `{Total|Total}\n{Percentage|100%}\n{Count|(${this.graphData.totalCount.toLocaleString('en-US')})}`,
                textAlign: "center",
                // Define the custom rich text styles for each line
                rich: {
                  Total: {
                    // Style for the word "Total"
                    fontSize: 16,
                    fontWeight: "bold",
                    color: "black",
                    lineHeight: 21, // Vertical spacing
                  },
                  Percentage: {
                    // Style for the "100%" line
                    fontSize: 24,
                    fontWeight: "bolder",
                    color: "black", // Green color for high visibility
                    lineHeight: 28,
                  },
                  Count: {
                    // Style for the actual count number (e.g., number of responses)
                    fontSize: 14,
                    fontWeight: "normal",
                    fill: "#003F72",
                    lineHeight: 19,
                  },
                },
              },
            },
          ],
        },
        // 2. Independent Graphic Line Divider (Visible on Desktop)
        {
          type: "line",
          left: "65%", // Positioned to visually divide the space
          top: "0%",
          shape: {
            x1: 0,
            y1: -100, // Top extent
            x2: 0,
            y2: 200, // Bottom extent
          },
          style: {
            stroke: "#E4E9F2",
            lineWidth: 2,
            opacity: 0.8,
          },
        },
      ],
      media: [
        {
          query: {
            maxWidth: 1380,
          },
          option: {


            graphic: [
              {
                type: "group",
                left: "29.5%",
                top: "18%",
                children: [
                  {
                    type: "circle",
                    shape: { r: 60 },
                    style: {
                      fill: "#ffffff",
                      shadowColor: "rgba(0,0,0,0.15)",
                      shadowBlur: 15,
                    },
                  },
                  {
                    type: "text",
                    left: "center",
                    top: "center",
                    style: {

                      text: `{Total|Total}\n{Percentage|100%}\n{Count|(${this.graphData.totalCount.toLocaleString('en-US')})}`,

                      // Default text styling
                      fill: "#003F72",
                      textAlign: "center",

                      // Define the custom rich text styles for each line
                      rich: {
                        Total: {
                          // Style for the word "Total"
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          lineHeight: 21, // Vertical spacing
                        },
                        Percentage: {
                          fontSize: 24,
                          fontWeight: "bolder",
                          color: "#000000",
                          lineHeight: 29,
                        },
                        Count: {

                          fontSize: 14,
                          fontWeight: "normal",
                          color: "#333333",
                          lineHeight: 19,
                        },
                      },
                    },
                  },
                ],
              },
              {
                type: "line",
                left: "65%", // Positioned to visually divide the space
                top: "0%",
                shape: {
                  x1: 0,
                  y1: -150, // Top extent
                  x2: 0,
                  y2: 150, // Bottom extent
                },
                style: {
                  stroke: "#E4E9F2",
                  lineWidth: 2,
                  opacity: 0.8,
                },
              }
            ],
          },
        },
        {
          query: {
            maxWidth: 1024,
          },
          option: {
            legend: {
              bottom: 0,
              left: "center",
              orient: "horizontal"
            },

            // B. Series/Chart: Shift the chart center slightly right
            series: [
              {
                name: "Mood",
                center: ["50%", "50%"], // Chart shifted right to 40%
                radius: ["60%", "70%"],
              },
            ],


            graphic: [
              {
                type: "group",
                left: "center", // Aligned for 40% center
                top: "center",
                children: [
                  {
                    type: "circle",
                    shape: { r: 100 },
                    style: {
                      fill: "#ffffff",
                      shadowColor: "rgba(0,0,0,0.15)",
                      shadowBlur: 15,
                    },
                  },
                  {
                    type: "text",
                    left: "center",
                    top: "center",
                    style: {

                      text: `{Total|Total}\n{Percentage|100%}\n{Count|(${this.graphData.totalCount})}`,
                      textAlign: "center",

                      // Define the custom rich text styles for each line
                      rich: {
                        Total: {
                          // Style for the word "Total"
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "black",
                          lineHeight: 22, // Vertical spacing
                        },
                        Percentage: {
                          fontSize: 24,
                          fontWeight: "bolder",
                          color: "black",
                          lineHeight: 26,
                        },
                        Count: {

                          fontSize: 14,
                          fontWeight: "normal",
                          color: "#003F72",
                          lineHeight: 20,
                        },
                      },
                    },
                  },
                ],
              },

            ],
          },
        },

        {
          query: {
            maxWidth: 430,
          },
          option: {
            legend: {
              orient: "horizontal",
              bottom: -2,
              itemGap: 2,
              textStyle: {
                fontSize: 10
              }

            },

            series: [
              {
                name: "Mood",
                center: ["50%", "40%"], // Center the chart horizontally and shift up
                radius: ["50%", "60%"], // Optionally reduce radius slightly
              },
            ],

            graphic: [
              {
                type: "group",
                left: "center",
                top: "25%",
                children: [
                  {
                    type: "circle",
                    shape: { r: 50 },
                    style: {
                      fill: "#ffffff",
                      shadowColor: "rgba(0,0,0,0.15)",
                      shadowBlur: 15,
                    },
                  },
                  {
                    type: "text",
                    left: "center",
                    top: "center",
                    style: {

                      text: `{Total|Total}\n{Percentage|100%}\n{Count|(${this.graphData.totalCount})}`,

                      // Default text styling
                      fill: "#003F72",
                      textAlign: "center",

                      // Define the custom rich text styles for each line
                      rich: {
                        Total: {
                          // Style for the word "Total"
                          fontSize: 14,
                          fontWeight: "bold",
                          color: "#000000",
                          lineHeight: 22, // Vertical spacing
                        },
                        Percentage: {
                          fontSize: 20,
                          fontWeight: "bolder",
                          color: "#000000",
                          lineHeight: 26,
                        },
                        Count: {

                          fontSize: 12,
                          fontWeight: "normal",
                          color: "#333333",
                          lineHeight: 20,
                        },
                      },
                    },
                  },
                ],
              },
              // 2. Independent Graphic Line Divider - Hide the vertical divider
              {
                type: "line",
                shape: {
                  x1: 0,
                  y1: -150,
                  x2: 0,
                  y2: 150,
                },
                style: {
                  stroke: "#E4E9F2",
                  lineWidth: 2,
                  opacity: 0, // Makes the vertical divider disappear on mobile
                },
              },
            ],
          },
        },

      ],
    };
  }
}
